<script setup lang="ts">
// validation
import { defineRule, useForm, useField } from 'vee-validate';
import { email, required } from '@vee-validate/rules';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiDynamicMarkdownComponentProps {
  topic: string;
  css?: string;
  name: string;
  desciption?: string;
}
const props = withDefaults(defineProps<CiDynamicMarkdownComponentProps>(), {});

// validation rules
defineRule('email', email);
defineRule('required', required);

const { $gettext, $pgettext } = useGettext();
const { locale } = useI18n();
const userStore = useUserStore();
const { openModal, closeModal } = useCiModal();

const loading = ref(false);
const success = ref(false);
const error = ref(false);
const isVerifiedResponseFromServer = ref(false);

const { validate } = useForm({
  validationSchema: {
    email: 'required|email',
  },
});

const { value: emailValue, errorMessage: emailError } = useField<string>('email');

const emailIsVerified = computed(() => (isVerifiedResponseFromServer.value || (userStore.user.profile?.user_status || 0) > 0));

function onClickShowLegalNotice() {
  const CiSubscribeLegalInfoModal = defineAsyncComponent({
    loader: () => import('@/components/slots/dynamic/CiSubscribeTopic/CiSubscribeLegalInfoModal.vue'),
  });

  openModal(
    {
      component: CiSubscribeLegalInfoModal,
      attrs: {
        name: 'CiSubscribeLegalInfoModal',
        onClose: () => {
          closeModal('CiSubscribeLegalInfoModal');
        },
      },
    },
    {
      clickToClose: true,
      contentTransition: 'translate-y-down',
      contentClass: 'max-w-[600px] w-full max-h-[800px] h-auto',
    },
  );
}

async function submit() {
  const { valid } = await validate();
  if (!valid) {
    return false;
  }

  const userService = useUserService();

  loading.value = true;
  success.value = false;
  error.value = false;
  isVerifiedResponseFromServer.value = false;

  userService.subscribeToSingleMarketingTopicByEmail(locale.value, emailValue.value, props.topic, userStore.insiderUid || undefined)
    .then((res) => {
      if (res !== null) {
        loading.value = false;
        success.value = true;
        isVerifiedResponseFromServer.value = res.is_verified;
      }
    })
    .catch((err) => {
      if (err != null) {
        loading.value = false;
        success.value = false;
        error.value = true;
      }
    });
  return true;
}

function restart() {
  loading.value = false;
  success.value = false;
  error.value = false;
}

function setEmailFromUserProfile() {
  if (emailValue.value) {
    return;
  }
  if (userStore.user.profile?.email) {
    emailValue.value = userStore.user.profile.email;
  }
}
setEmailFromUserProfile();
userStore.subscribeProfileUpdated(() => {
  setEmailFromUserProfile();
}, true);
onMounted(setEmailFromUserProfile);
</script>

<template>
  <div class="min-h-[3.125rem]" :class="props.css">
    <form v-if="!success && !error" novalidate @submit.prevent="submit">
      <div class="input-wrapper flex gap-2" :class="{ 'has-error': !!emailError }">
        <div class="grow">
          <input
            v-model="emailValue"
            :disabled="loading"
            class="input--inverted input-field input-field--text"
            type="email"
            name="email"
            autocomplete="email"
            :placeholder="$gettext('E-Mail')"
          />
          <div class="text-right text-sm">
            <a href="#" @click="onClickShowLegalNotice()">{{ $pgettext('Subscribe to newsletter', 'Legal notes') }}</a>
          </div>
        </div>
        <div class="pt-[5px]">
          <CiButton
            type="submit"
            tabindex="-1"
            :disabled="loading"
            :class="[loading ? 'button--disabled' : 'button--primary']"
            class="button"
          >
            <template #icon />
            {{ $pgettext('Subscribe to newsletter', 'Subscribe') }}
          </CiButton>
        </div>
      </div>
    </form>
    <div v-else>
      <CiInfoPanel
        v-if="error"
        :condition="true"
        type="danger"
        class="!m-0 flex items-center gap-2"
      >
        <span class="grow">{{ $gettext('An unknown error occurred. Please try again.') }}</span>
        <CiButton
          type="button"
          tabindex="-1"
          :disabled="loading"
          :class="[loading ? 'button--disabled' : 'button--primary']"
          class="button"
          @click="restart()"
        >
          <template #icon />
          <span class="text-nowrap">{{ $gettext('try again') }}</span>
        </CiButton>
      </CiInfoPanel>
      <CiInfoPanel
        v-else-if="success"
        :condition="true"
        type="success"
        class="!m-0"
      >
        <span v-if="emailIsVerified">
          {{ $gettext('Yay, it worked! 😍 We’re excited to have you on board!') }}
          <br />
          <br />
          {{ $gettext('From now on, you’ll receive amazing camping tips, trusted campsite recommendations, and lots of camping inspiration straight to your inbox.') }}
        </span>
        <span v-else>
          {{ $gettext('Yay – almost done! You’re just one small step away from receiving the best camping tips, verified site recommendations, and tons of inspiration regularly! 😍') }}
          <br />
          <br />
          {{ $gettext('In a few minutes, you’ll get an email from us – please check your inbox and confirm your subscription so you won’t miss any more camping tips.') }}
        </span>
      </CiInfoPanel>
    </div>
  </div>
</template>
